import { LLCTransaction } from '@models/llc-mortgages';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import * as actions from './llc-mortgage.actions';
import { Injectable } from '@angular/core';
import { LLCFunctions } from '@functions/llc.service';
import { CollectionPaths } from '@models/collections';
import {
	BusinessWithSubscriptionStateModel,
	FirestoreStateBaseForBusinessWithSubscription,
} from '@state/FirestoreStateBaseForBusiness';
import { Logger } from '@core/logger';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { firstValueFrom } from 'rxjs';

export interface LLCMortgageStateModel
	extends BusinessWithSubscriptionStateModel {
	items: LLCTransaction[];
	updating: boolean;
}
type Ctx = StateContext<LLCMortgageStateModel>;

const DEFAULT_STATE: LLCMortgageStateModel = {
	...DEFAULT_STATE_BASE,
	items: [],
	updating: false,
};

@State<LLCMortgageStateModel>({
	name: 'llcMortgages',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class LLCMortgageState extends FirestoreStateBaseForBusinessWithSubscription<
	LLCTransaction,
	LLCMortgageStateModel
> {
	@Selector([LLCMortgageState])
	static getLLCMortgages(state: LLCMortgageStateModel): LLCTransaction[] {
		return state?.items;
	}

	@Selector([LLCMortgageState])
	static getUpdating(state: LLCMortgageStateModel): boolean {
		return state?.updating;
	}

	@Selector([LLCMortgageState.getLLCMortgages])
	static getLLCMortgage(items: LLCTransaction[]) {
		return (id: string) => items?.find((item) => item.id === id);
	}

	constructor(private llcFunctions: LLCFunctions) {
		super(
			(pathParams) =>
				CollectionPaths.business_mortgages_llc_mortgages(pathParams.businessId),
			(doc) => ({ items: doc as LLCTransaction[] }),
			() => DEFAULT_STATE,
		);
	}

	@Action(actions.UpdateAll)
	updateAll({ patchState }: Ctx, { force }: actions.UpdateAll) {
		Logger.info('Fetching / Updating all LLC transactions');
		patchState({ updating: true });
		firstValueFrom(this.llcFunctions.updateAll(force))
			.catch((err) => Logger.exception(err, { force }))
			.finally(() => {
				patchState({ updating: false });
				Logger.info('Done fetching / Updating all LLC transactions');
			});
	}

	@Action(actions.UpdateStatus)
	updateStatus(ctx: Ctx, { transactionId }: actions.UpdateStatus): void {
		firstValueFrom(
			this.llcFunctions.updateTransactionStatus(transactionId),
		).catch((err) => Logger.exception(err, { transactionId }));
	}

	@Action(actions.LockUserAccess)
	lockUserAccess(ctx: Ctx, { transactionId }: actions.LockUserAccess): void {
		this.repo.CreateOrMerge(transactionId, {
			lastAccessUserId: this.authUser.uid,
			lastAccessTime: new Date().getTime(),
		});
	}

	@Action(actions.UnlockUserAccess)
	unlockUserAccess(
		ctx: Ctx,
		{ transactionId }: actions.UnlockUserAccess,
	): void {
		this.repo.CreateOrMerge(transactionId, {
			lastAccessUserId: null,
			lastAccessTime: null,
		});
	}

	@Action(actions.UpdateTimestamp)
	UpdateTimestamp(ctx: Ctx, { transactionId }: actions.UpdateTimestamp): void {
		this.repo.OverwriteFields(
			transactionId,
			'lastAccessTime',
			new Date().getTime(),
		);
	}
}
